import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { X, AlertTriangle, ArrowRight } from 'lucide-react';

// Adım isimleri
const STATUS_NAMES: Record<string, string> = {
  'draft': '1. Üretim (Taslak)',
  'production_entry': '2. Nebim (SKU Bekliyor)',
  'nebim_entry': '3. Nebim (İşlemde)',
  'barcode_sent': '4. Üretim (Üreticiye Gönderim)',
  'stock_entry': '5. Depo (Stok Girişi)',
  'api_transfer': '6. Yönetim (Ticimaxa Aktarım)',
  'content_entry': '7. İçerik (Açıklama)',
  'category_setup': '8. İçerik (Kategorileme)',
  'image_upload': '9. Görsel (Yükleme)',
  'publish_pending': '10. Yönetim (Yayın Bekliyor)',
  'published': '11. Yayında'
};

// Tüm adımlar sıralı (category_setup dahil)
const ALL_STATUSES = [
  'draft',
  'production_entry',
  'nebim_entry',
  'barcode_sent',
  'stock_entry',
  'api_transfer',
  'content_entry',
  'category_setup',
  'image_upload',
  'publish_pending',
  'published'
];

interface ForceStatusModalProps {
  productId: string;
  productName: string;
  currentStatus: string;
  onClose: () => void;
  onConfirm: (targetStatus: string, notes: string) => Promise<void>;
}

const ForceStatusModal: React.FC<ForceStatusModalProps> = ({
  productId,
  productName,
  currentStatus,
  onClose,
  onConfirm
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Edge case: Escape tuşu ile kapatma (loading değilse)
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  }, [loading, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleSubmit = async () => {
    // Edge case: Boş seçim kontrolü
    if (!selectedStatus) return;
    
    // Edge case: Loading sırasında çift tıklama koruması
    if (loading) return;
    
    setLoading(true);
    try {
      await onConfirm(selectedStatus, notes.trim());
      // Başarılı olursa parent (ProductTable) zaten modal'ı kapatıyor ve toast gösteriyor
    } catch (error) {
      console.error('Force status error:', error);
      // Hata durumunda loading'i kapat, modal açık kalsın ki kullanıcı tekrar deneyebilsin
      setLoading(false);
    }
  };

  // Edge case: Backdrop tıklaması ile kapatma (loading değilse)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  // Mevcut adımdan farklı olan adımları göster
  const availableStatuses = ALL_STATUSES.filter(s => s !== currentStatus);

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Adım Değiştir (Admin)
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Ürünü workflow'u atlayarak istediğiniz adıma atayın
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="pt-4 space-y-4">
          {/* Ürün Bilgisi */}
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-sm text-muted-foreground">Ürün</div>
            <div className="font-medium">{productName}</div>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs">
                {STATUS_NAMES[currentStatus] || currentStatus}
              </Badge>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <Badge variant="secondary" className="text-xs">
                {selectedStatus ? (STATUS_NAMES[selectedStatus] || selectedStatus) : 'Seçiniz...'}
              </Badge>
            </div>
          </div>

          {/* Hedef Adım Seçimi */}
          <div>
            <label className="block text-sm font-medium mb-2">Hedef Adım *</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Adım seçin...</option>
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {STATUS_NAMES[status] || status}
                </option>
              ))}
            </select>
          </div>

          {/* Not Alanı */}
          <div>
            <label className="block text-sm font-medium mb-2">Not (Opsiyonel)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Bu işlemin sebebini yazın..."
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Butonlar */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={loading}
            >
              İptal
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={handleSubmit}
              disabled={!selectedStatus || loading}
            >
              {loading ? 'İşleniyor...' : 'Onayla'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForceStatusModal;
